import React, { useCallback, useEffect, useState } from 'react'
import {
  adminMonitorService,
  MonitorOverview,
  MonitorSignal,
  MonitorPair,
  MonitorPosition,
  MonitorUser,
  MonitorUserHistory,
} from '@/services/admin_monitor.service'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

type Tab = 'signals' | 'pairs' | 'positions' | 'users'

const POLL_MS = 30000

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z')
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function StatusBadge({ label, tone }: { label: string; tone: 'green' | 'yellow' | 'red' | 'blue' | 'gray' }) {
  const map: Record<string, string> = {
    green: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
    yellow: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
    red: 'bg-red-500/15 text-red-500 border-red-500/30',
    blue: 'bg-sky-500/15 text-sky-500 border-sky-500/30',
    gray: 'bg-muted text-muted-foreground border-border',
  }
  return (
    <span className={`inline-block rounded border px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap ${map[tone]}`}>
      {label}
    </span>
  )
}

function execTone(status: string): 'green' | 'yellow' | 'red' | 'gray' {
  if (status === 'EXECUTED') return 'green'
  if (status === 'PENDING' || status === 'SENT') return 'yellow'
  if (status === 'EXPIRED' || status === 'FAILED' || status === 'BLOCKED') return 'red'
  return 'gray'
}

function KpiCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-xl border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-bold">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  )
}

export function SignalMonitor() {
  const [tab, setTab] = useState<Tab>('signals')
  const [overview, setOverview] = useState<MonitorOverview | null>(null)
  const [signals, setSignals] = useState<MonitorSignal[]>([])
  const [hours, setHours] = useState(24)
  const [pairs, setPairs] = useState<MonitorPair[]>([])
  const [positions, setPositions] = useState<MonitorPosition[]>([])
  const [users, setUsers] = useState<MonitorUser[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [userHistory, setUserHistory] = useState<MonitorUserHistory | null>(null)
  const [historyDays, setHistoryDays] = useState(7)
  const [loading, setLoading] = useState(true)
  const [userSearch, setUserSearch] = useState('')

  const refresh = useCallback(async () => {
    try {
      const [ov, sig, pr, pos, us] = await Promise.all([
        adminMonitorService.getOverview(),
        adminMonitorService.getSignals(hours),
        adminMonitorService.getPairs(),
        adminMonitorService.getPositions(),
        adminMonitorService.getUsers(),
      ])
      setOverview(ov)
      setSignals(sig.signals)
      setPairs(pr.pairs)
      setPositions(pos.positions)
      setUsers(us.users)
    } catch {
      // silenzioso: polling
    } finally {
      setLoading(false)
    }
  }, [hours])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, POLL_MS)
    return () => clearInterval(id)
  }, [refresh])

  const openUserHistory = async (userId: number, days = historyDays) => {
    try {
      const h = await adminMonitorService.getUserHistory(userId, days)
      setUserHistory(h)
    } catch {
      /* noop */
    }
  }

  const filteredUsers = users.filter(u =>
    !userSearch || u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.name.toLowerCase().includes(userSearch.toLowerCase()))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">📡 Monitor Segnali</h1>
        <div className="text-xs text-muted-foreground">
          Aggiornamento automatico ogni 30s{overview ? ` · ultimo: ${fmtTime(overview.generated_at)}` : ''}
        </div>
      </div>

      {/* KPI */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <KpiCard label="Segnali Makatsu oggi" value={overview.signals_today_makatsu} />
          <KpiCard label="Tentativi oggi" value={overview.dispatch_attempts_today} />
          <KpiCard label="Esecuzioni oggi" value={overview.executions_today} />
          <KpiCard
            label="Tasso esecuzione"
            value={overview.execution_rate_pct != null ? `${overview.execution_rate_pct}%` : '—'}
          />
          <KpiCard label="Skip oggi" value={overview.skips_today} />
          <KpiCard label="Posizioni aperte" value={overview.open_operations} />
          <KpiCard
            label="EA online"
            value={`${overview.ea_prop.online + overview.ea_broker.online}/${overview.ea_prop.total + overview.ea_broker.total}`}
            sub={`PROP ${overview.ea_prop.online}/${overview.ea_prop.total} · BROKER ${overview.ea_broker.online}/${overview.ea_broker.total}`}
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {([
          ['signals', 'Segnali'],
          ['pairs', 'Coppie / EA'],
          ['positions', 'Posizioni aperte'],
          ['users', 'Utenti'],
        ] as [Tab, string][]).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-muted-foreground">Caricamento…</div>}

      {/* SEGNALI */}
      {tab === 'signals' && !loading && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Finestra:</span>
            {[24, 48, 168].map(h => (
              <button
                key={h}
                onClick={() => setHours(h)}
                className={`rounded border px-2 py-1 text-xs ${hours === h ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
              >
                {h <= 48 ? `${h}h` : '7gg'}
              </button>
            ))}
            <span className="ml-auto text-xs text-muted-foreground">{signals.length} segnali</span>
          </div>

          {signals.map(s => (
            <div key={s.key} className="rounded-xl border bg-card">
              <button
                className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left"
                onClick={() => setExpanded(expanded === s.key ? null : s.key)}
              >
                <span className="text-xs text-muted-foreground w-28">{fmtTime(s.created_at)}</span>
                <StatusBadge label={s.engine} tone="blue" />
                {s.direction && (
                  <StatusBadge label={s.direction} tone={s.direction === 'BUY' ? 'green' : 'red'} />
                )}
                {s.entry_price != null && <span className="text-xs">@{s.entry_price}</span>}
                {s.zone_low != null && s.zone_high != null && (
                  <span className="text-xs">zona {s.zone_low}–{s.zone_high}</span>
                )}
                <span className="ml-auto flex items-center gap-2 text-xs">
                  <StatusBadge label={`${s.executed_count} eseguiti`} tone={s.executed_count > 0 ? 'green' : 'gray'} />
                  {s.attempted_count - s.executed_count > 0 && (
                    <StatusBadge label={`${s.attempted_count - s.executed_count} non riusciti`} tone="yellow" />
                  )}
                  {s.skipped_count > 0 && <StatusBadge label={`${s.skipped_count} saltati`} tone="red" />}
                  <span className="text-muted-foreground">{expanded === s.key ? '▲' : '▼'}</span>
                </span>
              </button>

              {expanded === s.key && (
                <div className="border-t px-3 py-2 space-y-3">
                  {s.executed.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold mb-1">Tentativi di esecuzione</div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Utente</TableHead>
                              <TableHead>Coppia</TableHead>
                              <TableHead>Dir.</TableHead>
                              <TableHead>Stato</TableHead>
                              <TableHead>Eseguito alle</TableHead>
                              <TableHead>Prezzo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {s.executed.map(e => (
                              <TableRow key={e.signal_id}>
                                <TableCell className="text-xs">{e.user || e.email || '—'}</TableCell>
                                <TableCell className="text-xs">#{e.pair_id} {e.provider}</TableCell>
                                <TableCell className="text-xs">{e.direction}</TableCell>
                                <TableCell><StatusBadge label={e.status} tone={execTone(e.status)} /></TableCell>
                                <TableCell className="text-xs">{fmtTime(e.executed_at)}</TableCell>
                                <TableCell className="text-xs">{e.prop_entry_price ?? '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  {s.skipped.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold mb-1">Saltati (con motivo)</div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Utente</TableHead>
                              <TableHead>Coppia</TableHead>
                              <TableHead>Motivo</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {s.skipped.map((k, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs">{k.user || k.email || '—'}</TableCell>
                                <TableCell className="text-xs">{k.pair_id ? `#${k.pair_id}` : '—'}</TableCell>
                                <TableCell className="text-xs text-muted-foreground break-all">{k.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                  {s.executed.length === 0 && s.skipped.length === 0 && (
                    <div className="text-xs text-muted-foreground">Nessun dettaglio disponibile.</div>
                  )}
                </div>
              )}
            </div>
          ))}
          {signals.length === 0 && <div className="text-sm text-muted-foreground">Nessun segnale nella finestra selezionata.</div>}
        </div>
      )}

      {/* COPPIE */}
      {tab === 'pairs' && !loading && (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utente</TableHead>
                <TableHead>Coppia</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>EA Prop</TableHead>
                <TableHead>EA Broker</TableHead>
                <TableHead>Makatsu</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pairs.map(p => (
                <TableRow key={p.pair_id}>
                  <TableCell className="text-xs">
                    <button className="underline decoration-dotted" onClick={() => { setTab('users'); openUserHistory(p.user_id) }}>
                      {p.user}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs">#{p.pair_id} {p.display_name || ''}</TableCell>
                  <TableCell className="text-xs">{p.provider}{p.control_mode ? ' · solo prop' : ''}</TableCell>
                  <TableCell>
                    <StatusBadge
                      label={p.status === 'ONLINE' ? 'ONLINE' : p.status === 'PARTIAL' ? 'PARZIALE' : 'OFFLINE'}
                      tone={p.status === 'ONLINE' ? 'green' : p.status === 'PARTIAL' ? 'yellow' : 'red'}
                    />
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.prop_online ? `✅ ${p.prop_last_seen_s}s fa` : p.prop_last_seen_s != null ? `❌ ${Math.round(p.prop_last_seen_s / 60)}min fa` : '❌ mai visto'}
                    {p.prop_trade_allowed === false && ' · trade OFF'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.control_mode ? '—' : p.broker_online ? `✅ ${p.broker_last_seen_s}s fa` : p.broker_last_seen_s != null ? `❌ ${Math.round(p.broker_last_seen_s / 60)}min fa` : '❌ mai visto'}
                  </TableCell>
                  <TableCell className="text-xs">{p.makatsu_enabled ? '魔 ON' : '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{p.prop_not_ready || ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* POSIZIONI */}
      {tab === 'positions' && !loading && (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Aperta</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Coppia</TableHead>
                <TableHead>Motore</TableHead>
                <TableHead>Dir.</TableHead>
                <TableHead>Ingresso</TableHead>
                <TableHead>Lotti P/B</TableHead>
                <TableHead>SL/TP $</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {positions.map(o => (
                <TableRow key={o.operation_id}>
                  <TableCell className="text-xs">{fmtTime(o.created_at)}</TableCell>
                  <TableCell className="text-xs">{o.user || o.email || '—'}</TableCell>
                  <TableCell className="text-xs">#{o.pair_id} {o.provider}</TableCell>
                  <TableCell className="text-xs">{o.engine || '—'}</TableCell>
                  <TableCell>
                    <StatusBadge label={o.direction} tone={o.direction === 'BUY' ? 'green' : 'red'} />
                  </TableCell>
                  <TableCell className="text-xs">{o.prop_open_price ?? '—'}</TableCell>
                  <TableCell className="text-xs">{o.prop_lots ?? '—'} / {o.broker_lots ?? '—'}</TableCell>
                  <TableCell className="text-xs">{o.sl_usd ?? '—'} / {o.tp_usd ?? '—'}</TableCell>
                  <TableCell><StatusBadge label={o.status} tone={o.status === 'ACTIVE' ? 'green' : 'yellow'} /></TableCell>
                </TableRow>
              ))}
              {positions.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-sm text-muted-foreground">Nessuna posizione aperta.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* UTENTI */}
      {tab === 'users' && !loading && (
        <div className="space-y-3">
          {!userHistory && (
            <>
              <input
                className="w-full max-w-sm rounded border bg-background px-3 py-1.5 text-sm"
                placeholder="Cerca per nome o email…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
              <div className="rounded-xl border bg-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Utente</TableHead>
                      <TableHead>Coppie attive</TableHead>
                      <TableHead>Tentativi oggi</TableHead>
                      <TableHead>Eseguiti oggi</TableHead>
                      <TableHead>Ultima esecuzione</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(u => (
                      <TableRow key={u.user_id}>
                        <TableCell className="text-xs">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-muted-foreground">{u.email}</div>
                        </TableCell>
                        <TableCell className="text-xs">{u.active_pairs}</TableCell>
                        <TableCell className="text-xs">{u.dispatches_today}</TableCell>
                        <TableCell className="text-xs">{u.executed_today}</TableCell>
                        <TableCell className="text-xs">{fmtTime(u.last_execution)}</TableCell>
                        <TableCell>
                          <button
                            className="rounded border px-2 py-1 text-xs hover:bg-muted"
                            onClick={() => openUserHistory(u.user_id)}
                          >
                            Storico →
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {userHistory && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button className="rounded border px-2 py-1 text-xs hover:bg-muted" onClick={() => setUserHistory(null)}>
                  ← Torna alla lista
                </button>
                <div>
                  <span className="font-semibold">{userHistory.user.name}</span>{' '}
                  <span className="text-xs text-muted-foreground">{userHistory.user.email}</span>
                </div>
                <div className="ml-auto flex items-center gap-1 text-xs">
                  {[7, 30, 60].map(d => (
                    <button
                      key={d}
                      onClick={() => { setHistoryDays(d); openUserHistory(userHistory.user.id, d) }}
                      className={`rounded border px-2 py-1 ${historyDays === d ? 'bg-primary text-primary-foreground' : 'bg-card'}`}
                    >
                      {d}gg
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-x-auto">
                <div className="px-3 pt-2 text-xs font-semibold">Segnali ricevuti / saltati</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Motore</TableHead>
                      <TableHead>Coppia</TableHead>
                      <TableHead>Dir.</TableHead>
                      <TableHead>Esito</TableHead>
                      <TableHead>Prezzo</TableHead>
                      <TableHead>Dettaglio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userHistory.history.map((h, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{fmtTime(h.at)}</TableCell>
                        <TableCell className="text-xs">{h.engine}</TableCell>
                        <TableCell className="text-xs">{h.pair_id ? `#${h.pair_id} ${h.provider || ''}` : '—'}</TableCell>
                        <TableCell className="text-xs">{h.direction || '—'}</TableCell>
                        <TableCell><StatusBadge label={h.status} tone={h.type === 'skip' ? 'red' : execTone(h.status)} /></TableCell>
                        <TableCell className="text-xs">{h.entry_price ?? '—'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground break-all max-w-[360px]">{h.detail || ''}</TableCell>
                      </TableRow>
                    ))}
                    {userHistory.history.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground">Nessun evento nel periodo.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-xl border bg-card overflow-x-auto">
                <div className="px-3 pt-2 text-xs font-semibold">Operazioni (aperte e chiuse)</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Aperta</TableHead>
                      <TableHead>Coppia</TableHead>
                      <TableHead>Motore</TableHead>
                      <TableHead>Dir.</TableHead>
                      <TableHead>Ingresso</TableHead>
                      <TableHead>Chiusura</TableHead>
                      <TableHead>P/L prop</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Motivo chiusura</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {userHistory.operations.map(o => (
                      <TableRow key={o.operation_id}>
                        <TableCell className="text-xs">{fmtTime(o.created_at)}</TableCell>
                        <TableCell className="text-xs">#{o.pair_id} {o.provider || ''}</TableCell>
                        <TableCell className="text-xs">{o.engine || '—'}</TableCell>
                        <TableCell className="text-xs">{o.direction}</TableCell>
                        <TableCell className="text-xs">{o.open_price ?? '—'}</TableCell>
                        <TableCell className="text-xs">{o.close_price ?? '—'}</TableCell>
                        <TableCell className={`text-xs font-medium ${o.pnl != null ? (o.pnl >= 0 ? 'text-emerald-500' : 'text-red-500') : ''}`}>
                          {o.pnl != null ? o.pnl.toFixed(2) : '—'}
                        </TableCell>
                        <TableCell>
                          <StatusBadge label={o.status} tone={o.status === 'CLOSED' ? 'gray' : 'green'} />
                        </TableCell>
                        <TableCell className="text-xs">{o.close_reason || ''}</TableCell>
                      </TableRow>
                    ))}
                    {userHistory.operations.length === 0 && (
                      <TableRow><TableCell colSpan={9} className="text-sm text-muted-foreground">Nessuna operazione nel periodo.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
